import { useContext, useEffect, useRef } from 'react';
import {
    JsonHubProtocol,
    HubConnectionState,
    HubConnectionBuilder,
    LogLevel,
    HubConnection,
    IHttpConnectionOptions,
    HttpRequest,
    HttpClient,
} from '@microsoft/signalr';
import { useAuthStore } from '../context/AuthContext';
import { useConnectionStore } from '../context/ConnectionContext';
import { HttpResponse } from '@microsoft/signalr/dist/esm/HttpClient';
import { useAolUpdateHandler } from './useAolUpdateHandler';
import { AolAddedDTO, AolSectionSignalRDTO, AolSectionSignalRUpdate } from '../types/Dto/AolSectionSignalRDTO';
import { AolSectionDataUpdateDto } from '../types/Dto/AolSectionDataUpdateDto';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import useEvent from './useEvent';
import { ValidatedNoonReportFactory } from '../factory/ValidatedNoonReportFactory';
import { ValidatedNoonReportBM } from '../types/BusinessModel/ValidatedNoonReportBM';
import { ImosPortCallSyncDto } from '../types/Dto/ImosPortCallSyncDto';
import { useToast } from './useToast';
import { useVesselStore } from '../context/VesselContext';
import { SignalRResponse } from '../types/Dto/SignalRResponse';
import { SignalRMessageType } from '../types/Dto/SignalRMessageType';
import { useSWService } from '../api/sw/useSWService';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from '../api/endpoints/queryParamsHelper';
import useApi from '../api/useApi';
import { useAolStore } from '../context/AolContext';
import { makeAolDataItemList, makeAolGroupList } from '../factory/Aol/AolDataItemFactory';
import useGetAolData from '../api/aol/useGetAolData';
import { GetSectionBM } from '../utils/aolSectionHelper';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getStringValue } from '../utils/arrayUtils';
import useCargoTemperatureApi from '../api/cargo-temp-log/useCargoTempLogApi';
import getContext from '../context/AppSettingsContext';
import { formatAolUrlDate } from '../utils/aol/aolDateUtils';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import useAppFeature from './useAppFeature';
import { useCargoTempLogStore } from '../context/CargoTempLogContext';
import { CargoTemperatureLogDate } from '../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { useBunkerUpdateHandler } from './useBunkerUpdateHandler';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { getAllReportEventsItems } from 'src/api/report-events/offlineReportEventsApi';
import { getAllFuelRobItems } from 'src/api/bunkering/offlineBunkeringApi';
import { useRotationStore } from 'src/context/RotationContext';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { useStowagePlanResponseHandler } from 'src/api/responseHandlers/useStowagePlanResponseHandler';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { useAppStatus } from '../context/AppStatusContext';

export default () => {
    const { setSignalRConnectionId, loggedInUser } = useAuthStore();
    const { selectedVessel } = useVesselStore();
    const { refetch: refetchAppStatus } = useAppStatus();
    const { setConnectionStatus, hubConnection, setHubConnection, connectedVesselGroup, setConnectedVesselGroup, isOfflineSimulated } =
        useConnectionStore();
    const messageQueueRef = useRef<SignalRResponse[]>([]);
    const isProcessingRef = useRef(false);
    const reconciliationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const processMessageQueueRef = useRef<() => Promise<void>>();
    // Mirrors the latest selectedVessel so long-lived callbacks created once (e.g. connection.onreconnected)
    // never act on a stale closure value.
    const selectedVesselRef = useRef(selectedVessel);
    selectedVesselRef.current = selectedVessel;
    const { updateAolList, addNoonReport, updateOnDelete, updateAolStatusToOtherUsers, toggleToast, updateOnHoursSync, updateOnImosPortCallIdSync, reconcileGroupStatuses } =
        useAolUpdateHandler();
    const { handleUpdatedBunkering, handleUpdatedBunkeringStatus, handleDeletedBunkeringStatus, handleUpdatedRobs, toggleBunkerToast } =
        useBunkerUpdateHandler();
    const { toastSuccess, toastError } = useToast();
    const { updateDeltaEventArray } = useEvent();
    const { refreshRequestsContaining, removeRequestsContaining } = useSWService();
    const { apiEndpoints } = useApiEndpoints();
    const { getNoonReportSection, getAolItems } = useGetAolData();
    const { getMasterStowagePlanFromApi } = useGetMasterPlanApi();
    const { setAolItems, aolFilter, setHasSyncError } = useAolStore();
    const { cargoTempLogDates } = useCargoTempLogStore();
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const { getApiData, postApiData } = useApi();
    const location = useLocation();
    const { getCargoTemperatureConfigs, getCargoTemperatureInputFields } = useCargoTemperatureApi();
    const appSettings = useContext(getContext());
    const { updateAolMissingTasks } = useUpdateMissingTasksIcon();
    const { isFeatureEnabled } = useAppFeature();
    const { productList, portNameList, berthNameList, stowagePlanRotationPortCallList, stowagePlanRotationCargoList } = useRotationStore();
    const { activeStowagePlan } = useStowagePlanStore();

    const MAX_SIGNALR_RETRY_ATTEMPTS = 15;
    const { onStowagePlanUpdated, onStowagePlanAdded, updateLoadedMasterStowagePlan } = useStowagePlanResponseHandler();

    const getUpdatedSection = async (sectionSignalRUpdate: AolSectionSignalRUpdate, message: SignalRResponse) => {
        if (!selectedVessel?.imoNumber || !loggedInUser) {
            return;
        }
        const updatedNoonReport = await getNoonReportSection(
            selectedVessel.imoNumber,
            0,
            sectionSignalRUpdate.section,
            true,
            sectionSignalRUpdate.noonReportGuid
        );
        const updatedSection = GetSectionBM(sectionSignalRUpdate.section, updatedNoonReport);

        const dto: AolSectionSignalRDTO = {
            section: updatedSection,
            noonReportId: updatedNoonReport?.metadata?.noonReportId,
            noonReportGuid: sectionSignalRUpdate.noonReportGuid,
            imoNumber: sectionSignalRUpdate.imoNumber,
            indexInDay: 0,
        };
        updateAolList(dto);
        toggleToast(dto.noonReportId ?? 0, message, hubConnection);
    };

    const getDistinctCargoNumbers = (logDates: CargoTemperatureLogDate[]): string[] => {
        const cargoNumbersSet = new Set<string>();

        logDates.forEach((logDate) => {
            logDate.cargoInTankList?.forEach((cargoInTank) => {
                cargoNumbersSet.add(cargoInTank.cargoNumber);
            });
        });

        return Array.from(cargoNumbersSet);
    };

    const eventMessageHandler = async (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as TankersEventDataBM;
        if (dto.events && dto.events.length > 0) {
            const offlineReportEvents = await getAllReportEventsItems();

            refreshRequestsContaining(
                [apiEndpoints['getTankersEventDataDeltaForVoyage'].url],
                'voyageNumber1=' + dto.events[0].voyageNumber + '&imoNumber=' + dto.imoNumber
            );
            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyageList'].url]);
            updateDeltaEventArray(dto, offlineReportEvents);
            toastSuccess(message.sender + ' has updated events');
        }
    };

    const aolSectionMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionSignalRDTO;

        // Use throttled refresh instead of direct call
        throttledRefreshNoonReports(dto.imoNumber);

        updateAolList(dto);
        toggleToast(+dto.noonReportId, message, hubConnection);
    };

    const syncNoonReportFuelRobMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionSignalRDTO;

        // Use throttled refresh instead of direct call
        throttledRefreshNoonReports(dto.imoNumber);

        if (location.pathname === '/aol') {
            setHasSyncError(false);

            const fromDate = formatAolUrlDate(aolFilter.fromDate);
            const toDate = formatAolUrlDate(aolFilter.toDate);
            const aolItemsPromise = getAolItems(dto.imoNumber, fromDate, toDate, true);
            const noonReportsPromise = getNoonReports(dto.imoNumber, fromDate, toDate);
            const loadedMasterStowagePlanPromise = getMasterStowagePlanFromApi(selectedVessel);

            Promise.all([aolItemsPromise, noonReportsPromise, loadedMasterStowagePlanPromise]).then(async (values) => {
                setAolItems([...values[0]]);
                const _aolDataItems = await makeAolDataItemList(values[0], values[1], isFeatureEnabled, selectedVessel, values[2]?.data);
                const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
                updateAolList(dto, { aolDataItems: _aolDataItems, aolGroups: _aolGroups });
                console.log('updateAolList has been called');
                toggleToast(+dto.noonReportId, message, hubConnection);
            });
        }
    };

    const aolSectionUpdateMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const sectionSignalRUpdate = text as AolSectionSignalRUpdate;
        getUpdatedSection(sectionSignalRUpdate, message);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(sectionSignalRUpdate.imoNumber);
    };

    const aolSectionDataUpdateMessageToOtherUsersHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolSectionDataUpdateDto;
        updateAolStatusToOtherUsers(dto, message.sender);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(dto.imoNumber);

        // Schedule trailing reconciliation for terminal transitions
        if (dto.isAolApproved || dto.isAolCompleted) {
            scheduleReconciliationRefresh();
        }
    };

    const addAolMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as AolAddedDTO;
        addNoonReport(validatedNoonReportFactory.create(dto.noonReport as ValidatedNoonReportBM));

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(dto.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(dto.imoNumber);
        }
    };

    const deleteAolMessageHandler = (message: SignalRResponse) => {
        const deletePayload = JSON.parse(message.responseObject);
        updateOnDelete(undefined, deletePayload.noonReportGuid);
        toastError('An AOL was deleted by another user.');

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(+message.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(+message.imoNumber);
        }
    };

    const syncNoonReportPortLogHoursMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const noonReportBM = text as ValidatedNoonReportBM;
        const noonReport = validatedNoonReportFactory.create(noonReportBM);
        updateOnHoursSync(noonReport);
        toggleToast(noonReport.metadata.noonReportId, message, hubConnection);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(noonReport.metadata.imoNumber);
    };

    const syncNoonReportImosPortCallIdMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const dto = text as ImosPortCallSyncDto;
        updateOnImosPortCallIdSync(0, +dto.imosPortCallId, dto.noonReportGuid);
        toggleToast(0, message, hubConnection);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(+message.imoNumber);
    };

    const saveCargoTemperatureConfigsMessageHandler = (message: SignalRResponse) => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
                getCargoTemperatureInputFields(selectedVessel?.imoNumber, true);
                toastSuccess(message?.sender + ' has updated cargo temp. configuration');
            }
        }
    };

    const saveCargoTemperatureLogsMessageHandler = (_message: SignalRResponse) => {
        // Refresh cargo temp log dates for other users when someone saves a log
        refreshRequestsContaining([apiEndpoints['getCargoTempLogDates'].url]);
    };

    const deleteCargoTemperatureConfigsMessageHandler = (_message: SignalRResponse) => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
            }
        }
    };

    const upsertBunkeringMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const bunkeringBM = text as BunkeringBM;
        handleUpdatedBunkering(bunkeringBM);
        toggleBunkerToast(message, hubConnection, 'bunkering data');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertBunkeringStatusMessageHandler = (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const bunkeringStatusBM = text as BunkeringStatusBM;
        handleUpdatedBunkeringStatus(bunkeringStatusBM);
        toggleBunkerToast(message, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const deleteBunkeringStatusMessageHandler = (message: SignalRResponse) => {
        const bunkeringStatusGuid = message.responseObject as string;
        handleDeletedBunkeringStatus(bunkeringStatusGuid);
        toggleBunkerToast(message, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertRobsMessageHandler = async (message: SignalRResponse) => {
        const text = JSON.parse(message.responseObject);
        const fuelRobData = text as FuelRobBM[];
        const offlineFuelRobs = await getAllFuelRobItems();

        handleUpdatedRobs(fuelRobData, offlineFuelRobs);
        toggleBunkerToast(message, hubConnection, 'ROB data');
        refreshRequestsContaining([apiEndpoints['getFuelRobData'].url]);
    };

    const upsertBunkerLossMessageHandler = (message: SignalRResponse) => {
        toggleBunkerToast(message, hubConnection, 'bunker loss data');
        refreshRequestsContaining([apiEndpoints['getBunkerLossData'].url]);
    };

    const imosUpdatesHandler = (message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const updatedVoyages = message.responseObject as number[];

            console.log('IMOS data has been updated...', updatedVoyages);
            toastSuccess('IMOS data has been updated...');

            if (!activeStowagePlan) {
                return;
            }

            refreshRequestsContaining([apiEndpoints['getRotation'].url], null, {
                productList,
                portNameList,
                berthNameList,
                selectedVessel,
                stowagePlanGuid: activeStowagePlan.stowagePlanGuid,
                stowagePlanTransitOperations: activeStowagePlan.transitOperations,
                stowagePlanRotationPortCallList,
                stowagePlanRotationCargoList,
            });
        }
    };
    const stowagePlanUpdatedHandler = (message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(message.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            if (
                updatedStowagePlanBM.type === StowagePlanTypeEnum.Master &&
                isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: new Date() })
            ) {
                // Handle master stowage plan change
                updateLoadedMasterStowagePlan(updatedStowagePlanBM, message?.sender);
                refreshRequestsContaining([apiEndpoints['getMasterStowagePlan'].url]);
            }

            if (location.pathname === '/stowage') {
                onStowagePlanUpdated(updatedStowagePlanBM, message?.sender);
                refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                refreshRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            } else {
                removeRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                removeRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            }
        }
    };

    const stowagePlanAddedHandler = (message: SignalRResponse) => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(message.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            onStowagePlanAdded(updatedStowagePlanBM, message?.sender);
            refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
        }
    };

    type MessageHandler = (message: SignalRResponse) => void | Promise<void>;

    const handleSignalRResponse = async (responseType: SignalRMessageType, signalRResponse: SignalRResponse, hubConnection: HubConnection) => {
        const responseHandlers: { [key in SignalRMessageType]?: { otherUsersHandler: MessageHandler; senderHandler: MessageHandler | null } } = {
            event: { otherUsersHandler: eventMessageHandler, senderHandler: null },
            aolSection: { otherUsersHandler: aolSectionMessageHandler, senderHandler: null },
            syncNoonReportFuelRob: { otherUsersHandler: syncNoonReportFuelRobMessageHandler, senderHandler: null },
            aolSectionUpdate: { otherUsersHandler: aolSectionUpdateMessageHandler, senderHandler: null },
            aolSectionDataUpdate: {
                otherUsersHandler: aolSectionDataUpdateMessageToOtherUsersHandler,
                senderHandler: null,
            },
            addAol: { otherUsersHandler: addAolMessageHandler, senderHandler: null },
            deleteAol: { otherUsersHandler: deleteAolMessageHandler, senderHandler: null },
            syncNoonReportPortLogHours: { otherUsersHandler: syncNoonReportPortLogHoursMessageHandler, senderHandler: null },
            syncNoonReportImosPortCallId: { otherUsersHandler: syncNoonReportImosPortCallIdMessageHandler, senderHandler: null },
            saveCargoTemperatureConfigs: { otherUsersHandler: saveCargoTemperatureConfigsMessageHandler, senderHandler: null },
            upsertBunkering: { otherUsersHandler: upsertBunkeringMessageHandler, senderHandler: null },
            upsertBunkeringStatus: { otherUsersHandler: upsertBunkeringStatusMessageHandler, senderHandler: null },
            deleteBunkeringStatus: { otherUsersHandler: deleteBunkeringStatusMessageHandler, senderHandler: null },
            upsertRobs: { otherUsersHandler: upsertRobsMessageHandler, senderHandler: null },
            upsertBunkerLoss: { otherUsersHandler: upsertBunkerLossMessageHandler, senderHandler: null },
            imosUpdates: { otherUsersHandler: imosUpdatesHandler, senderHandler: null },
            stowagePlanUpdated: { otherUsersHandler: stowagePlanUpdatedHandler, senderHandler: null },
            stowagePlanAdded: { otherUsersHandler: stowagePlanAddedHandler, senderHandler: null },
            deleteCargoTemperatureConfigs: { otherUsersHandler: deleteCargoTemperatureConfigsMessageHandler, senderHandler: null },
            saveCargoTemperatureLogs: { otherUsersHandler: saveCargoTemperatureLogsMessageHandler, senderHandler: null },
        };

        const handlers = responseHandlers[responseType];
        if (handlers) {
            if (signalRResponse.connectionId !== hubConnection.connectionId) {
                await handlers.otherUsersHandler?.(signalRResponse);
            } else {
                await handlers.senderHandler?.(signalRResponse);
            }
        }
    };

    const scheduleReconciliationRefresh = () => {
        if (reconciliationTimerRef.current) {
            clearTimeout(reconciliationTimerRef.current);
        }
        reconciliationTimerRef.current = setTimeout(() => {
            reconciliationTimerRef.current = null;
            reconcileGroupStatuses();
        }, 2000);
    };

    const processMessageQueue = async () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            while (messageQueueRef.current.length > 0) {
                const message = messageQueueRef.current.shift()!;

                // Global broadcast messages — no IMO filter
                if (message.type === 'appStatusChanged') {
                    refetchAppStatus();
                    continue;
                }

                if (hubConnection && message.imoNumber === selectedVessel?.imoNumber?.toString()) {
                    await handleSignalRResponse(message.type, message, hubConnection);
                } else if (message.imoNumber !== selectedVessel?.imoNumber?.toString()) {
                    console.log(
                        'Rejecting SignalR message: signalRResponse.imoNumber = ' +
                            message.imoNumber +
                            ' and selectedVessel?.imoNumber = ' +
                            selectedVessel?.imoNumber
                    );
                }
            }
        } finally {
            isProcessingRef.current = false;
        }
    };

    // Keep ref in sync so the event listener always calls the latest version
    processMessageQueueRef.current = processMessageQueue;

    // Cleanup reconciliation timer on unmount
    useEffect(() => {
        return () => {
            if (reconciliationTimerRef.current) {
                clearTimeout(reconciliationTimerRef.current);
            }
        };
    }, []);

    const getNoonReports = async (imoNumber: number, fromDate: string, toDate: string) => {
        // Create a unique key for this request
        const requestKey = `SignalR:getNoonReportsForShip:${imoNumber}:${fromDate}:${toDate}`;

        // Use throttle manager for deduplication — bypass cooldown since SignalR signals fresh data
        return requestThrottleManager.executeBypassCooldown(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getNoonReportsForShip'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache: true,
            });

            if (result) {
                return validatedNoonReportFactory.createList(result);
            }
            return [];
        });
    };

    /**
     * Optimized handler for refreshing GetNoonReportsForShip endpoint
     * Uses deduplication to prevent multiple parallel calls
     */
    const throttledRefreshNoonReports = (imoNumber?: number) => {
        if (!imoNumber && selectedVessel) {
            imoNumber = selectedVessel.imoNumber;
        }

        if (!imoNumber) return;

        // Create a request-specific key based on the current vessel
        const requestKey = `refreshNoonReports:${imoNumber}`;

        // Use executeBypassCooldown to ensure SignalR-triggered refreshes always fetch fresh data
        // Still deduplicates in-flight requests, so rapid SignalR messages won't cause parallel refreshes
        requestThrottleManager.executeBypassCooldown(requestKey, async () => {
            refreshRequestsContaining([apiEndpoints['getNoonReportsForShip'].url]);
            return true;
        });
    };

    const setupSignalRConnection = (url: string, imoNumber: number) => {
        const customHttpClient: HttpClient = new (class extends HttpClient {
            async send(request: HttpRequest): Promise<HttpResponse> {
                try {
                    let response;
                    if (request.method === 'POST') {
                        response = await postApiData({
                            endpoint: request.url,
                            disableAppSettingsApiEndpoint: true,
                            returnFullResponseObject: true,
                            data: request.content,
                        });
                    } else if (request.method === 'GET') {
                        response = await axios.get(request.url);
                    } else if (request.method === 'DELETE') {
                        response = await axios.delete(request.url);
                    } else {
                        response = await axios({ method: request.method?.toLowerCase(), url: request.url, data: request.content });
                    }

                    if (!response) {
                        return new HttpResponse(500, 'Internal Error', 'Request failed');
                    }

                    const responseBody = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                    if (response.status === 200 && response.data?.accessToken) {
                        localStorage.setItem('signalRConnection', responseBody);
                    }
                    return new HttpResponse(response.status, response.statusText, responseBody);
                } catch (error) {
                    return new HttpResponse(500, 'Internal Error', error?.message || 'Request failed');
                }
            }
        })();

        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => {
                return '';
            },
            httpClient: customHttpClient,
        };

        const connection = new HubConnectionBuilder()
            .withUrl(url, options)
            .withAutomaticReconnect()
            .withHubProtocol(new JsonHubProtocol())
            .configureLogging(LogLevel.Information)
            .build();

        console.log('Connection: ' + connection.connectionId);
        // Note: to keep the connection open the serverTimeout should be
        // larger than the KeepAlive value that is set on the server
        // keepAliveIntervalInMilliseconds default is 15000 and we are using default
        // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
        connection.serverTimeoutInMilliseconds = 60000;

        // re-establish the connection if connection dropped
        connection.onclose((error) => {
            console.assert(connection.state === HubConnectionState.Disconnected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Disconnected);
            }
            setSignalRConnectionId('');
            console.log('Connection closed due to error. Try refreshing this page to restart the connection.', error);
        });

        connection.onreconnecting((error) => {
            console.assert(connection.state === HubConnectionState.Reconnecting);
            console.log('Connection lost due to error. Reconnecting...', error);
        });

        connection.onreconnected((connectionId) => {
            console.assert(connection.state === HubConnectionState.Connected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Connected);
            }
            setSignalRConnectionId(connectionId ?? '');
            console.log('Connection re-established. Connected with connectionId: ', connectionId);

            // The Azure SignalR group membership is tied to the previous connectionId, which is
            // now invalid. Rejoin unconditionally using the current vessel (via ref, since this
            // closure was created once and must not rely on a stale selectedVessel value) —
            // connectedVesselGroup state cannot be trusted here because it still reflects the old
            // (now-invalid) group membership rather than the new connection's actual state.
            const imoNumber = selectedVesselRef.current?.imoNumber;
            if (imoNumber) {
                console.log('Rejoining vessel group after reconnect for imoNumber:', imoNumber);
                joinVesselGroup(connection, imoNumber);
            } else {
                console.warn('SignalR reconnected but no vessel selected — skipping group rejoin');
            }
        });

        startSignalRConnection(connection, url, imoNumber);
        return connection;
    };

    const startSignalRConnection = async (connection: HubConnection, url: string, imoNumber: number, attempts = 0) => {
        try {
            await throwErrorIfOfflineIsSimulated();
            if (connection) {
                await connection.start();

                if (connection.connectionId && connection.state === HubConnectionState.Connected) {
                    if (setConnectionStatus) {
                        setConnectionStatus(HubConnectionState.Connected);
                    }
                    setSignalRConnectionId(connection.connectionId);
                    setHubConnection(connection);

                    // Register message listener immediately so global broadcasts
                    // (e.g., appStatusChanged) are received before vessel group join
                    connection.on('newMessage', (res) => {
                        messageQueueRef.current.push(res);
                        processMessageQueueRef.current?.();
                    });
                }
            }
        } catch (error) {
            if (await isOfflineSimulated()) return;
            console.error('SignalR Connection Error: ', error);

            if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                console.warn('SignalR: Max connection attempts reached, entering dormant mode');
                toastError('Connection lost — will auto-reconnect when online');
                return;
            }

            const delay = attempts < 5 ? 5000 : 30000;
            setTimeout(() => startSignalRConnection(connection, url, imoNumber, attempts + 1), delay);
        }
    };

    /**
     * Joins the given vessel group on the current connection, retrying with backoff on failure.
     * Unlike tryConnectToGroup, this always attempts the join — it does not gate on
     * connectedVesselGroup — because it's also used to re-establish group membership after a
     * reconnect, when connectedVesselGroup state cannot be trusted to reflect the new
     * connection's actual server-side membership.
     */
    const joinVesselGroup = async (connection: HubConnection, imoNumber: number, attempts = 0) => {
        if (!imoNumber || !connection || connection.state !== HubConnectionState.Connected) return;
        connectToGroup(connection, appSettings.api, imoNumber)
            .then(() => {
                setConnectedVesselGroup(imoNumber);
            })
            .catch(async () => {
                if (await isOfflineSimulated()) return;
                console.log('Failed to join SignalR group, retrying');

                if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                    console.warn('SignalR: Max group join attempts reached, entering dormant mode');
                    toastError('Connection lost — will auto-reconnect when online');
                    return;
                }

                const delay = attempts < 5 ? 5000 : 30000;
                setTimeout(() => joinVesselGroup(connection, imoNumber, attempts + 1), delay);
            });
    };

    const tryConnectToGroup = async (connection: HubConnection, imoNumber: number, attempts = 0) => {
        if (!imoNumber || !connection || connection.state !== HubConnectionState.Connected) return;
        if (connectedVesselGroup !== imoNumber) {
            await joinVesselGroup(connection, imoNumber, attempts);
        }
    };

    useEffect(() => {
        if (selectedVessel && hubConnection && hubConnection.state === HubConnectionState.Connected) {
            if (connectedVesselGroup && connectedVesselGroup !== selectedVessel.imoNumber) {
                leaveGroup(hubConnection, appSettings.api, connectedVesselGroup).finally(() =>
                    tryConnectToGroup(hubConnection, selectedVessel.imoNumber)
                );
            } else {
                tryConnectToGroup(hubConnection, selectedVessel.imoNumber);
            }
        }
    }, [hubConnection, selectedVessel]);

    const leaveGroup = async (connection: HubConnection, url: string, imoNumber: number) => {
        return await getApiData({
            endpoint: url + 'LeaveGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
    };

    const stopSignalRConnection = () => {
        if (hubConnection) {
            hubConnection.stop();
            setConnectionStatus(HubConnectionState.Disconnected);
            localStorage.setItem('signalRConnection', '');
        }
    };

    const connectToGroup = async (connection: HubConnection, url: string, imoNumber: number) => {
        return await getApiData({
            endpoint: url + 'JoinGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
    };

    const throwErrorIfOfflineIsSimulated = async () => {
        const _isOfflineSimulated = await isOfflineSimulated();
        if (_isOfflineSimulated) throw new Error('offline');
    };

    return { setupSignalRConnection, stopSignalRConnection };
};
